import { useReducer, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  initialManageRecipeVariantState,
  manageRecipeVariantReducer
} from "./manageRecipeVariantReducer";
import { useManageRecipeVariantData } from "./useManageRecipeVariantData";
import { useManageRecipeVariantDispatch } from "./useManageRecipeVariantDispatch";
import { useManageRecipeVariantEffects } from "./useManageRecipeVariantEffects";
import { useManageRecipeVariantForm } from "./useManageRecipeVariantForm";
import { useManageRecipeVariantHandlers } from "./useManageRecipeVariantHandlers";

export const useManageRecipeVariant = (recipeId: any) => {
  const location = useLocation();
  const isViewOnly = location.state?.viewOnly === true;

  const [state, dispatch] = useReducer(manageRecipeVariantReducer, initialManageRecipeVariantState);
  const hasOpenedNewRecipeModalRef = useRef(false);

  const {
    pendingDeleteVariantIds,
    visibleModal,
    visibleUpdateModal,
    visibleRecipeUpdateModal,
    ingredientVisibleModal,
    processingStepVisibleModal,
    processingStepViewModal,
    processingStepEditModal,
    selectedProcess,
    deleteIngredient,
    isOriginal,
    variants,
    isNewRecipe,
    variantId,
    deleteVariant,
    scrollToBottom,
    scrollToTop,
    isAtBottom,
    tenantId,
    selectedVariants
  } = state;

  const dispatchActions = useManageRecipeVariantDispatch(dispatch, selectedVariants, isAtBottom);

  const data = useManageRecipeVariantData(recipeId, tenantId);

  const form = useManageRecipeVariantForm({
    recipeData: data.recipeData,
    variants,
    staticRecipeIngredientList: data.staticRecipeIngredientList
  });

  const handlers = useManageRecipeVariantHandlers({
    recipeId,
    dispatch,
    recipeData: data.recipeData,
    recipeDataRefetch: data.recipeDataRefetch,
    refetchRecipeResults: data.refetchRecipeResults,
    pendingDeleteVariantIds,
    deleteIngredient,
    selectedVariants,
    setSelectedVariants: dispatchActions.setSelectedVariants,
    productSize: data.productSize,
    recipeResults: data.recipeResults,
    impactDetails: data.impactDetails,
    variants,
    staticRecipeIngredientList: data.staticRecipeIngredientList,
    deleteRecipeVariant: data.deleteRecipeVariant,
    getPdfUrl: data.getPdfUrl,
    calculateRecipe: data.calculateRecipe,
    deleteRecipeIngredient: data.deleteRecipeIngredient,
    updateIngredientPercentage: data.updateIngredientPercentage
  });

  useManageRecipeVariantEffects({
    recipeId,
    dispatch,
    hasOpenedNewRecipeModalRef,
    recipeData: data.recipeData,
    location,
    isRecipeDataLoading: data.isRecipeDataLoading,
    isFetching: data.isFetching,
    isLoadingStaticRecipeIngredients: data.isLoadingStaticRecipeIngredients,
    debouncedUpdateIngredientPercentage: handlers.debouncedUpdateIngredientPercentage
  });

  return {
    isViewOnly,
    isLoading: data.isLoading,
    isNewRecipe,
    recipeData: data.recipeData,
    recipeResults: data.recipeResults,
    recipeDataRefetch: data.recipeDataRefetch,
    genericProcess: data.genericProcess,
    usedColors: form.usedColors,
    ingredientNames: form.ingredientNames,
    control: form.control,
    errors: form.errors,
    totals: form.totals,
    fixedColumnsWithRealTimeUpdates: form.fixedColumnsWithRealTimeUpdates,
    dynamicColumnsWithRealTimeUpdates: form.dynamicColumnsWithRealTimeUpdates,
    variants,
    hasAnyUpdates: form.hasAnyUpdates,
    productSize: data.productSize,
    setProductSize: data.setProductSize,
    selectedVariants,
    setSelectedVariants: dispatchActions.setSelectedVariants,
    handleSubmit: form.handleSubmit,
    onSaveAndCalculate: handlers.onSaveAndCalculate,
    downloadPdf: handlers.downloadPdf,
    handleToggleScroll: dispatchActions.handleToggleScroll,
    onButtonAction: dispatchActions.onButtonAction,
    debouncedUpdateIngredientPercentage: handlers.debouncedUpdateIngredientPercentage,
    handleRemoveVariant: handlers.handleRemoveVariant,
    scrollToBottom,
    scrollToTop,
    handleScrollPositionChange: dispatchActions.handleScrollPositionChange,
    visibleModal,
    setVisibleModal: dispatchActions.setVisibleModal,
    visibleUpdateModal,
    setVisibleUpdateModal: dispatchActions.setVisibleUpdateModal,
    visibleRecipeUpdateModal,
    setVisibleRecipeUpdateModal: dispatchActions.setVisibleRecipeUpdateModal,
    ingredientVisibleModal,
    setIngredientVisibleModal: dispatchActions.setIngredientVisibleModal,
    processingStepVisibleModal,
    setProcessingStepVisibleModal: dispatchActions.setProcessingStepVisibleModal,
    processingStepViewModal,
    setProcessingStepViewModal: dispatchActions.setProcessingStepViewModal,
    processingStepEditModal,
    setProcessingStepEditModal: dispatchActions.setProcessingStepEditModal,
    selectedProcess,
    setSelectedProcess: dispatchActions.setSelectedProcess,
    deleteIngredient,
    setDeleteIngredient: dispatchActions.setDeleteIngredient,
    isOriginal,
    setIsOriginal: dispatchActions.setIsOriginal,
    variantId,
    setVariantId: dispatchActions.setVariantId,
    deleteVariant,
    setDeleteVariant: dispatchActions.setDeleteVariant,
    handleDeleteIngredient: handlers.handleDeleteIngredient,
    deleteVariants: handlers.deleteVariants,
    navigateDetailsToEdit: dispatchActions.navigateDetailsToEdit
  };
};
